import React, { useMemo, useState } from "react";
import { Input, Select, Space, Table, Tag, Typography } from "antd";
import { formatTraceNumber, dashIfEmpty } from "../utils/priceTraceFormatters";

const { Text } = Typography;

const PLAN_COMPARE_FIELDS = new Set([
  "commission_starter",
  "commission_pro",
  "final_price_starter",
  "final_price_pro",
]);

function cellNum(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number" && Number.isFinite(v)) return formatTraceNumber(v);
  if (typeof v === "string" && v.length > 80) return `${v.slice(0, 77)}…`;
  return String(v);
}

function planComparisonBadges(record) {
  if (record.field === "unified_hr_margin" && record.meta) {
    return (
      <Tag key="unified-hr" className="apt-badge-unified-hr">
        Unified HR
      </Tag>
    );
  }
  if (!PLAN_COMPARE_FIELDS.has(record.field) || !record.meta) return null;
  const m = record.meta;
  const tags = [];
  if (record.field === "commission_starter" || record.field === "commission_pro") {
    tags.push(
      <Tag key="plan" className="apt-badge-plan">
        Plan
      </Tag>
    );
  }
  if (m.is_applied === true) {
    tags.push(
      <Tag key="active" className="apt-badge-active">
        Active Plan
      </Tag>
    );
  }
  const cs = m.compare_status;
  if (cs === "cheaper") {
    tags.push(
      <Tag key="cheaper" className="apt-badge-cheaper">
        Cheaper
      </Tag>
    );
  } else if (cs === "higher") {
    tags.push(
      <Tag key="higher" className="apt-badge-higher">
        Higher
      </Tag>
    );
  } else if (cs === "equal") {
    tags.push(
      <Tag key="equal" className="apt-badge-equal">
        Equal
      </Tag>
    );
  }
  return tags.length ? <Space size={4} wrap>{tags}</Space> : null;
}

function rowClassForPlanTrace(record) {
  const parts = ["apt-row"];
  if (record.anomalyFlag) {
    parts.push("apt-row--anomaly");
    return parts.join(" ");
  }
  if (record.priceChanged) {
    parts.push("apt-row--changed");
  }
  const m = record.meta;
  if (m?.is_applied === true) {
    parts.push("apt-row-active-plan");
  }
  const f = record.field;
  if (f === "final_price_starter" || f === "final_price_pro") {
    if (m?.compare_status === "cheaper") parts.push("apt-row-cheaper");
    else if (m?.compare_status === "higher") parts.push("apt-row-higher");
    else if (m?.compare_status === "equal") parts.push("apt-row-equal");
  }
  return parts.join(" ");
}

export default function AdminPriceTraceTable({ steps }) {
  const [layerFilter, setLayerFilter] = useState("all");
  const [anomalyFilter, setAnomalyFilter] = useState("all");
  const [q, setQ] = useState("");

  const layers = useMemo(() => {
    const set = new Set();
    (steps || []).forEach((s) => s.layer && set.add(s.layer));
    return ["all", ...Array.from(set).sort()];
  }, [steps]);

  const filtered = useMemo(() => {
    let rows = Array.isArray(steps) ? [...steps] : [];
    if (layerFilter !== "all") {
      rows = rows.filter((r) => r.layer === layerFilter);
    }
    if (anomalyFilter === "anomaly_only") {
      rows = rows.filter((r) => r.anomalyFlag || r.anomaly);
    }
    const needle = q.trim().toLowerCase();
    if (needle) {
      rows = rows.filter((r) => {
        const hay = [
          r.layer,
          r.field,
          r.planKey,
          r.notes,
          r.sourceFile,
          r.sourceFunction,
          r.anomaly,
          String(r.rawValue),
          String(r.normalized),
          r.meta != null ? JSON.stringify(r.meta) : "",
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(needle);
      });
    }
    return rows;
  }, [steps, layerFilter, anomalyFilter, q]);

  const columns = [
    {
      title: "Step",
      dataIndex: "step",
      width: 64,
      sorter: (a, b) => (a.step || 0) - (b.step || 0),
    },
    {
      title: "Layer",
      dataIndex: "layer",
      width: 120,
      render: (v) => <Tag className="apt-layer-tag">{dashIfEmpty(v)}</Tag>,
      sorter: (a, b) => String(a.layer).localeCompare(String(b.layer)),
    },
    {
      title: "Field",
      dataIndex: "field",
      width: 200,
      ellipsis: true,
      render: (_, record) => (
        <Space size={[4, 4]} wrap align="start" className="apt-field-cell">
          <span className="apt-field-name">{record.field}</span>
          {planComparisonBadges(record)}
        </Space>
      ),
    },
    {
      title: "Raw",
      dataIndex: "rawValue",
      width: 100,
      render: (v) => <span className="apt-mono">{cellNum(v)}</span>,
    },
    {
      title: "Normalized",
      dataIndex: "normalized",
      width: 100,
      render: (v) => <span className="apt-mono">{cellNum(v)}</span>,
    },
    { title: "Currency", dataIndex: "currency", width: 88, ellipsis: true },
    {
      title: "FX",
      dataIndex: "exchangeRate",
      width: 88,
      render: (v) => <span className="apt-mono">{cellNum(v)}</span>,
    },
    { title: "Rooms", dataIndex: "rooms", width: 72, render: cellNum },
    { title: "Pax", dataIndex: "passengers", width: 72, render: cellNum },
    {
      title: "Comm %",
      dataIndex: "commissionPercent",
      width: 72,
      render: cellNum,
    },
    {
      title: "Comm",
      dataIndex: "commissionAmount",
      width: 80,
      render: (v) => <span className="apt-mono">{cellNum(v)}</span>,
    },
    { title: "Markup", dataIndex: "markup", width: 72, render: cellNum },
    { title: "Discount", dataIndex: "discount", width: 72, render: cellNum },
    {
      title: "Before round",
      dataIndex: "beforeRound",
      width: 96,
      render: (v) => <span className="apt-mono">{cellNum(v)}</span>,
    },
    {
      title: "After round",
      dataIndex: "afterRound",
      width: 96,
      render: (v) => <span className="apt-mono">{cellNum(v)}</span>,
    },
    {
      title: "Display",
      dataIndex: "display",
      width: 96,
      render: (v) => <span className="apt-mono">{cellNum(v)}</span>,
    },
    {
      title: "Anomaly",
      dataIndex: "anomaly",
      width: 100,
      render: (v) => (v ? <Tag color="red">{v}</Tag> : "—"),
    },
    {
      title: "Notes",
      dataIndex: "notes",
      width: 220,
      ellipsis: true,
      render: (v) => <Text type="secondary">{dashIfEmpty(v)}</Text>,
    },
    { title: "Source file", dataIndex: "sourceFile", width: 160, ellipsis: true },
    {
      title: "Source fn",
      dataIndex: "sourceFunction",
      width: 140,
      ellipsis: true,
    },
  ];

  return (
    <div className="apt-table-wrap">
      <Space wrap className="apt-table-toolbar" style={{ marginBottom: 8 }}>
        <Input.Search
          allowClear
          placeholder="חיפוש טקסט בכל העמודות"
          onChange={(e) => setQ(e.target.value)}
          style={{ width: 240 }}
        />
        <Select
          value={layerFilter}
          onChange={setLayerFilter}
          options={layers.map((l) => ({ value: l, label: l === "all" ? "כל השכבות" : l }))}
          style={{ width: 160 }}
        />
        <Select
          value={anomalyFilter}
          onChange={setAnomalyFilter}
          options={[
            { value: "all", label: "כל השורות" },
            { value: "anomaly_only", label: "חריגות בלבד" },
          ]}
          style={{ width: 140 }}
        />
      </Space>
      <Table
        size="small"
        bordered
        pagination={{ pageSize: 12, showSizeChanger: true, pageSizeOptions: [12, 24, 48] }}
        rowKey="key"
        scroll={{ x: 2200 }}
        dataSource={filtered}
        columns={columns}
        rowClassName={rowClassForPlanTrace}
        expandable={{
          expandedRowRender: (record) => {
            const notesBlock = record.notes ? (
              <div style={{ marginBottom: record.meta != null ? 12 : 0 }}>{record.notes}</div>
            ) : null;
            const isPlanFinal =
              record.field === "final_price_starter" || record.field === "final_price_pro";
            const m = record.meta;
            const planFinalBlock =
              isPlanFinal && m ? (
                <dl className="apt-expand-plan-meta">
                  <dt>תוכנית</dt>
                  <dd>{String(m.plan ?? "—")}</dd>
                  <dt>הוחל</dt>
                  <dd>{String(m.is_applied)}</dd>
                  <dt>השוואה מול תוכנית</dt>
                  <dd>{String(m.compare_to_plan ?? "—")}</dd>
                  <dt>סטטוס השוואה</dt>
                  <dd>{String(m.compare_status ?? "—")}</dd>
                  <dt>מחיר משווה</dt>
                  <dd>{cellNum(m.compared_price)}</dd>
                  <dt>פער מחיר</dt>
                  <dd>{m.price_gap != null ? cellNum(m.price_gap) : "—"}</dd>
                </dl>
              ) : null;
            const metaJson =
              record.meta != null ? JSON.stringify(record.meta, null, 2) : null;
            if (!notesBlock && !metaJson && !planFinalBlock) return null;
            return (
              <div className="apt-expand-notes">
                {notesBlock}
                {planFinalBlock}
                {metaJson ? (
                  <pre className="apt-expand-meta" style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                    {metaJson}
                  </pre>
                ) : null}
              </div>
            );
          },
          rowExpandable: (record) =>
            !!(record.meta != null) ||
            !!(record.notes && String(record.notes).length > 60),
        }}
      />
    </div>
  );
}
